import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Image,
  Modal,
  Row,
  Table,
} from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL;

const emptyForm = {
  title: "",
  image_url: "",
  link_url: "",
  status: 1,
  display_order: 0,
  start_at: "",
  end_at: "",
};

const toDatetimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const toApiDatetime = (value) => (value ? value.replace("T", " ") + ":00" : null);

function AdManagement() {
  const [ads, setAds] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/ads`);
      setAds(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (ad) => {
    setEditingId(ad.id);
    setForm({
      title: ad.title || "",
      image_url: ad.image_url || "",
      link_url: ad.link_url || "",
      status: Number(ad.status) ? 1 : 0,
      display_order: ad.display_order || 0,
      start_at: toDatetimeLocal(ad.start_at),
      end_at: toDatetimeLocal(ad.end_at),
    });
    setShowModal(true);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    setError(null);

    const payload = {
      ...form,
      status: Number(form.status) ? 1 : 0,
      display_order: Number(form.display_order) || 0,
      start_at: toApiDatetime(form.start_at),
      end_at: toApiDatetime(form.end_at),
    };

    try {
      if (editingId) {
        await axios.put(`${API_URL}/ads/${editingId}`, payload);
        setMessage("Quảng cáo đã được cập nhật.");
      } else {
        await axios.post(`${API_URL}/ads`, payload);
        setMessage("Quảng cáo đã được thêm.");
      }

      setShowModal(false);
      await fetchAds();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (ad) => {
    try {
      setMessage(null);
      setError(null);
      await axios.put(`${API_URL}/ads/${ad.id}/status`, {
        status: Number(ad.status) ? 0 : 1,
      });
      setMessage("Trạng thái quảng cáo đã được cập nhật.");
      fetchAds();
    } catch (err) {
      setError(err);
    }
  };

  const handleDelete = async (ad) => {
    if (!window.confirm(`Xóa quảng cáo "${ad.title}"?`)) return;

    try {
      setMessage(null);
      setError(null);
      await axios.delete(`${API_URL}/ads/${ad.id}`);
      setMessage("Quảng cáo đã được xóa.");
      fetchAds();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <Container className="mt-4">
      <div className="admin-page-intro">
        <div>
          <span>Popup client</span>
          <h2>Quản lý quảng cáo</h2>
          <p>Quảng cáo đang bật sẽ hiển thị dạng popup ảnh ở giữa màn hình khi người dùng vào trang client.</p>
        </div>
        <div className="d-flex align-items-start">
          <Button variant="primary" onClick={openCreateModal}>
            Thêm quảng cáo
          </Button>
        </div>
      </div>

      {message && <Alert variant="success">{message}</Alert>}
      {error && (
        <Alert variant="danger">
          Lỗi: {error.response?.data || error.message}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {loading ? (
            <p className="mb-0">Đang tải quảng cáo...</p>
          ) : (
            <Table striped bordered hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Ảnh</th>
                  <th>Tên</th>
                  <th>Link</th>
                  <th>Thứ tự</th>
                  <th>Trạng thái</th>
                  <th>Hành động</th>
                </tr>
              </thead>
              <tbody>
                {ads.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">
                      Chưa có quảng cáo.
                    </td>
                  </tr>
                )}
                {ads.map((ad) => (
                  <tr key={ad.id}>
                    <td style={{ width: 120 }}>
                      <Image
                        src={ad.image_url}
                        alt={ad.title}
                        thumbnail
                        style={{ width: 96, height: 58, objectFit: "cover" }}
                      />
                    </td>
                    <td>
                      <strong>{ad.title}</strong>
                      <div className="text-muted small">
                        Cập nhật: {new Date(ad.updated_at).toLocaleString()}
                      </div>
                    </td>
                    <td>
                      {ad.link_url ? (
                        <a href={ad.link_url} target="_blank" rel="noreferrer">
                          Mở link
                        </a>
                      ) : (
                        <span className="text-muted">Không có</span>
                      )}
                    </td>
                    <td>{ad.display_order}</td>
                    <td>
                      <Form.Check
                        type="switch"
                        id={`ad-status-${ad.id}`}
                        label={Number(ad.status) ? "Đang bật" : "Đang tắt"}
                        checked={Number(ad.status) === 1}
                        onChange={() => handleToggleStatus(ad)}
                      />
                      {Number(ad.status) ? (
                        <Badge bg="success">Hiển thị</Badge>
                      ) : (
                        <Badge bg="secondary">Ẩn</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => openEditModal(ad)}
                      >
                        Sửa
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(ad)}
                      >
                        Xóa
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{editingId ? "Sửa quảng cáo" : "Thêm quảng cáo"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={7}>
                <Form.Group className="mb-3">
                  <Form.Label>Tên quảng cáo</Form.Label>
                  <Form.Control
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>URL ảnh</Form.Label>
                  <Form.Control
                    name="image_url"
                    value={form.image_url}
                    onChange={handleChange}
                    placeholder="https://..."
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Link khi bấm vào ảnh</Form.Label>
                  <Form.Control
                    name="link_url"
                    value={form.link_url}
                    onChange={handleChange}
                    placeholder="https://..."
                  />
                </Form.Group>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Thứ tự</Form.Label>
                      <Form.Control
                        type="number"
                        name="display_order"
                        value={form.display_order}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Trạng thái</Form.Label>
                      <Form.Check
                        type="checkbox"
                        label="Bật quảng cáo"
                        name="status"
                        checked={Number(form.status) === 1}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bắt đầu</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        name="start_at"
                        value={form.start_at}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Kết thúc</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        name="end_at"
                        value={form.end_at}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              <Col md={5}>
                <Form.Label>Preview</Form.Label>
                <div className="border rounded p-2 bg-light">
                  {form.image_url ? (
                    <Image
                      src={form.image_url}
                      alt="Preview quảng cáo"
                      fluid
                      style={{ maxHeight: 320, objectFit: "contain", width: "100%" }}
                    />
                  ) : (
                    <div className="text-muted text-center py-5">
                      Nhập URL ảnh để xem preview.
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Hủy
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? "Đang lưu..." : "Lưu quảng cáo"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default AdManagement;
